"use client";

import { useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { AlertCircle, Loader2, ShieldCheck, X } from "lucide-react";

import { RootState, useAppDispatch, useAppSelector } from "@/lib/redux/root";
import { clearAuthError } from "@/lib/redux/slices/auth.slice";
import useFormAuth from "@/lib/hooks/useFormAuth";
import { useBrand } from "@/lib/hooks/useBrand";
import { setOtpModalVisible } from "@/lib/redux/slices/common.slice";

export function OtpVerificationModal() {
	const dispatch = useAppDispatch();

	const {
		location: { appInfo },
	} = useAppSelector((state: RootState) => state.location);

	const { brandConfig } = useBrand();
	const { email } = useAppSelector((state: RootState) => state.uploadForm);
	const { otpModalVisible } = useAppSelector(
		(state: RootState) => state.common
	);
	const { loading, error } = useAppSelector((state: RootState) => state.auth);

	const {
		otpInputRefs,
		otp,
		handleOtpChange,
		handleOtpKeyDown,
		handlePaste,
		handleOTPResend,
		resendTimer,
		setResendTimer,
		isResending,
		resetOtp,
	} = useFormAuth();

	const wasVisible = useRef(false);

	/* -------------------- RESET ON OPEN -------------------- */
	useEffect(() => {
		if (otpModalVisible && !wasVisible.current) {
			setResendTimer(30);
			resetOtp();
			dispatch(clearAuthError());
		}
		wasVisible.current = otpModalVisible;
	}, [otpModalVisible]);

	/* -------------------- CLOSE HANDLER -------------------- */

	const handleClose = () => {
		if (loading) return;
		dispatch(setOtpModalVisible(false));
	};

	/* -------------------- COUNTDOWN -------------------- */
	useEffect(() => {
		if (resendTimer <= 0) return;
		const timer = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
		return () => clearTimeout(timer);
	}, [resendTimer]);

	if (!otpModalVisible) {
		return null;
	}

	const primaryColor = appInfo?.primaryColor || brandConfig.primaryColor;

	return (
		<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm'>
			<Card className='animate-in fade-in zoom-in-95 relative w-full max-w-md border-none bg-white shadow-2xl duration-200 sm:max-w-lg'>
				<button
					type='button'
					onClick={handleClose}
					disabled={loading}
					className='absolute top-4 right-4 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50'
				>
					<X className='h-6 w-6' />
				</button>
				<div className='flex flex-col items-center px-6 py-8 sm:px-10 sm:py-10'>
					{/* Icon */}
					<div
						className='mb-6 flex h-16 w-16 items-center justify-center rounded-full sm:h-20 sm:w-20'
						style={{ backgroundColor: `${primaryColor}15` }}
					>
						<ShieldCheck
							className='h-8 w-8 sm:h-10 sm:w-10'
							style={{ color: primaryColor }}
						/>
					</div>

					{/* Title */}
					<div className='mb-6 text-center'>
						<h2 className='mb-2 text-xl font-bold text-gray-800 sm:text-2xl'>
							Verify Your Email
						</h2>
						<p className='text-sm text-gray-500 sm:text-base'>
							Enter the 6-digit code sent to
						</p>
						<p className='inline-block max-w-full font-semibold break-words break-all text-slate-900'>
							{email}
						</p>
					</div>

					{/* OTP Inputs */}
					<div className='mb-6 flex w-full justify-center gap-2 sm:gap-3'>
						{otp.map((digit, i) => (
							<Input
								key={i}
								ref={(el) => {
									otpInputRefs.current[i] = el;
								}}
								type='text'
								inputMode='numeric'
								maxLength={1}
								value={digit}
								disabled={loading}
								autoFocus={i === 0}
								onChange={(e) => handleOtpChange(i, e.target.value)}
								onKeyDown={(e) => handleOtpKeyDown(e, i)}
								onPaste={handlePaste}
								className='h-12 w-10 rounded-lg border-2 border-gray-300 p-0 text-center text-xl leading-none font-bold transition-all disabled:bg-gray-50 sm:h-16 sm:w-14 sm:text-2xl'
								onFocus={(e) => {
									e.currentTarget.style.borderColor = primaryColor;
									e.currentTarget.style.boxShadow = `0 0 0 3px ${primaryColor}25`;
								}}
								onBlur={(e) => {
									e.currentTarget.style.borderColor = "";
									e.currentTarget.style.boxShadow = "";
								}}
							/>
						))}
					</div>

					{/* Error */}
					{error && (
						<div className='mb-4 flex w-full items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-red-700'>
							<AlertCircle className='h-5 w-5 shrink-0' />
							<p className='text-sm font-medium'>{error}</p>
						</div>
					)}

					{/* Loading / Verifying */}
					{(loading || isResending) && (
						<div className='mb-4 flex w-full items-center gap-3 rounded-lg bg-blue-50 px-4 py-3'>
							<Loader2 className='h-5 w-5 animate-spin text-blue-600' />
							<span className='text-sm font-medium text-blue-700'>
								{loading ? "Verifying code..." : "Sending new code..."}
							</span>
						</div>
					)}

					{/* Resend */}
					<div className='text-center'>
						<p className='text-sm text-gray-600'>
							Didn't receive the code?{" "}
							<button
								type='button'
								onClick={handleOTPResend}
								disabled={loading || isResending || resendTimer > 0}
								className='font-semibold transition hover:underline disabled:pointer-events-none disabled:text-gray-400'
								style={{
									color:
										loading || isResending || resendTimer > 0
											? undefined
											: primaryColor,
								}}
							>
								{isResending
									? "Sending..."
									: resendTimer > 0
										? `Resend in ${resendTimer}s`
										: "Resend Code"}
							</button>
						</p>
					</div>
				</div>
			</Card>
		</div>
	);
}
